import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api.js'

/**
 * Per-user UI preferences (server-side, so they follow the person across
 * devices): collapsed sections, expanded cards, hidden projects, etc.
 * `usePref(key, default)` returns [value, set]; sets are optimistic and merged
 * shallowly on the server (PATCH /me/ui-prefs).
 */
export type Prefs = Record<string, unknown>

export function usePrefs() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['ui-prefs'], queryFn: () => api<{ prefs: Prefs }>('/me/ui-prefs'), staleTime: 5 * 60_000 })
  const save = useMutation({ mutationFn: (patch: Prefs) => api('/me/ui-prefs', { method: 'PATCH', json: patch }) })
  const set = useCallback((patch: Prefs) => {
    qc.setQueryData<{ prefs: Prefs }>(['ui-prefs'], (old) => ({ prefs: { ...(old?.prefs ?? {}), ...patch } }))
    save.mutate(patch)
  }, [qc, save])
  return { prefs: q.data?.prefs ?? {}, loaded: !!q.data, set }
}

export function usePref<T>(key: string, fallback: T): [T, (v: T) => void, boolean] {
  const { prefs, loaded, set } = usePrefs()
  const value = (prefs[key] as T | undefined) ?? fallback
  return [value, (v: T) => set({ [key]: v }), loaded]
}
